// THE PLAIN TEXT OF ONE ANSWER LINE — what a ticked line is SAVED as (Pouria, 2026-10-01).
//
// A search answer is markdown, and each line the user ticks is saved as an Extraction: its
// `md` draws the line on screen, its `content` is what the pack keeps. Until now the plain version
// only lost its leading bullet and its `**`, so a price line from the answer —
//
//     $170 — [gearpatrol.com](https://www.gearpatrol.com/…) [5] · "…"
//
// — was saved exactly like that, and the raw link and the citation marker showed up in the
// Work-board chip, on the rail, in the pack document, and in the heading the document builds out of
// the text (lib/packDocExtractions.js `extractionHeading`). Found on the 2026-10-01 QA pass of the
// search → save → pack journey.
//
// So the saved text is the line as it READS: a link becomes its words, the "[5]" citation markers
// (the answer's pointers into its own source list) go, and the remaining emphasis marks go. Nothing
// is thrown away silently: the link's address is handed back beside the text, and the saved item
// carries it in `url` — the research item's own source field, which every save path already sends
// (store/modules/ongoing.js `researchSaveItem`). A line citing several pages keeps the first; the
// original markdown stays on the turn (`md`) for as long as the session does.
//
// …and a comparison's `[[better]]` marks (busy-ai-srv#41, lib/compareMarkers.js) go too: they are the
// page's instruction to draw a pill, not words anyone wrote.

import { stripCompareMarkers } from '@/lib/compareMarkers'

// [words](https://address "optional title")
const LINK = /\[([^\]]*)\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"]*")?\s*\)/g
// <https://address> — a bare autolink.
const AUTOLINK = /<(https?:\/\/[^>\s]+)>/g
// [5], [1, 3], [2-4], [^1] — a citation marker (never a link: those were replaced above).
const CITE = /\s*\[\^?\d+(?:\s*[,–-]\s*\d+)*\]/g

/**
 * Inline markdown → the words it draws. Links become their text (or their address when they have
 * no text), citation markers go, emphasis/code marks go. Leading list/heading markers are left
 * alone — that is `answerLineText`'s job, for a whole line.
 *
 * @param {string} md
 * @returns {string}
 */
export function inlineMarkdownToText(md) {
  return stripCompareMarkers(String(md || ''))
    .replace(LINK, (m, text, url) => (text.trim() || url))
    .replace(AUTOLINK, '$1')
    .replace(CITE, '')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/(^|[^\w*])\*(?!\s)([^*]+?)\*(?!\w)/g, '$1$2')
    .replace(/(^|[^\w])_(?!\s)([^_]+?)_(?!\w)/g, '$1$2')
    .replace(/`+/g, '')
    .replace(/\*\*/g, '')
    // …and what the removals leave behind: doubled spaces, a space before punctuation, a
    // separator with nothing on one side of it.
    .replace(/[ \t]+/g, ' ')
    .replace(/\s+([,.;:!?)])/g, '$1')
    .replace(/(^|\s)[·•|—–-](\s+[·•|—–-])+(?=\s|$)/g, '$1·')
    .replace(/\s[·•|—–-]\s*$/, '')
    .trim()
}

/** The first web address a line links to, or ''. */
export function firstLinkUrl(md) {
  const s = String(md || '')
  for (const m of s.matchAll(LINK)) if (/^https?:\/\//i.test(m[2])) return m[2]
  const auto = s.match(/<(https?:\/\/[^>\s]+)>/)
  return auto ? auto[1] : ''
}

/**
 * One answer line → `{ text, url }`: the plain text it is saved as, and the page it cites (''
 * when none). The leading list bullet / heading / numbering goes first — only as a MARKER (followed
 * by a space), so a line that starts with a year or a price keeps it.
 *
 * @param {string} md one line of the answer, as written
 * @returns {{ text: string, url: string }}
 */
export function answerLineText(md) {
  const line = String(md || '').trim().replace(/^(?:(?:#{1,6}|[-*•+]|\d+[.)])\s+)+/, '')
  return { text: inlineMarkdownToText(line), url: firstLinkUrl(line) }
}

export default answerLineText
